import { useMemo } from 'react';
import { Download, ExternalLink, FileText, Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  formatStatementDate,
  formatStatementMoney,
  getLineItemTypeLabel,
  shouldShowStatementStatus,
} from '../utils/billingStatement';

const SummaryCard = ({ label, amount, currency }) => (
  <div className='rounded-lg border border-border bg-muted/30 p-4'>
    <p className='text-xs text-muted-foreground mb-1'>{label}</p>
    <p className='text-lg font-semibold text-foreground'>{formatStatementMoney(amount, currency)}</p>
  </div>
);

const REFUND_LINE_TYPES = new Set(['refund', 'refund_payout']);

const typeBadgeClass = (type) => {
  if (REFUND_LINE_TYPES.has(type)) return 'bg-info-bg text-info border-transparent';
  if (type === 'credit_note') return 'bg-success-bg text-success border-transparent';
  return '';
};

/**
 * Resolve a PDF / hosted URL for a line item — invoices use their own links;
 * refund lines use the related invoice when present in the same statement.
 */
const resolveLineDocumentUrl = (item, invoiceDocsById) => {
  if (item.invoicePdfUrl || item.hostedInvoiceUrl) {
    return item.invoicePdfUrl || item.hostedInvoiceUrl;
  }
  if (REFUND_LINE_TYPES.has(item.type) && item.relatedInvoiceId) {
    const doc = invoiceDocsById.get(item.relatedInvoiceId);
    return doc?.invoicePdfUrl || doc?.hostedInvoiceUrl || null;
  }
  return null;
};

const BillingStatementView = ({
  statement,
  loading,
  generatingPdf,
  onDownloadPdf,
  showStripeCustomerId = false,
}) => {
  const currency = statement?.currency;
  const summary = statement?.summary || {};
  const lineItems = statement?.lineItems || [];

  const invoiceDocsById = useMemo(() => {
    const map = new Map();
    for (const item of lineItems) {
      if (item.type === 'invoice' && item.stripeObjectId) {
        map.set(item.stripeObjectId, {
          invoicePdfUrl: item.invoicePdfUrl,
          hostedInvoiceUrl: item.hostedInvoiceUrl,
        });
      }
    }
    return map;
  }, [lineItems]);

  if (loading && !statement) {
    return (
      <div className='flex justify-center py-8'>
        <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (!statement) {
    return (
      <p className='text-sm text-muted-foreground py-4'>
        Select a period and generate a statement to view account activity.
      </p>
    );
  }

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-start justify-between gap-4'>
        <div>
          <h3 className='text-base font-semibold text-foreground'>{statement.organisation?.name}</h3>
          {statement.organisation?.vatNumber && (
            <p className='text-sm text-muted-foreground'>VAT: {statement.organisation.vatNumber}</p>
          )}
          <p className='text-sm text-muted-foreground mt-1'>
            Period: {formatStatementDate(statement.period?.from)} – {formatStatementDate(statement.period?.to)}
          </p>
          {showStripeCustomerId && statement.stripeCustomerId && (
            <p className='text-xs text-muted-foreground mt-1'>Stripe customer: {statement.stripeCustomerId}</p>
          )}
          {statement.contractingEntity?.legalName && (
            <p className='text-xs text-muted-foreground mt-1'>
              Billed by {statement.contractingEntity.legalName}
            </p>
          )}
        </div>
        {onDownloadPdf && (
          <Button variant='outline' size='sm' onClick={onDownloadPdf} loading={generatingPdf} className='gap-2'>
            <Download size={16} strokeWidth={1.5} />
            Download PDF
          </Button>
        )}
      </div>

      <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3'>
        <SummaryCard label='Opening balance' amount={summary.openingBalance} currency={currency} />
        <SummaryCard label='Total invoiced' amount={summary.totalInvoiced} currency={currency} />
        <SummaryCard label='Total paid' amount={summary.totalPaid} currency={currency} />
        <SummaryCard label='Total credits' amount={summary.totalCredits} currency={currency} />
        <SummaryCard label='Total refunded' amount={summary.totalRefunded} currency={currency} />
        <SummaryCard label='Closing balance' amount={summary.closingBalance} currency={currency} />
      </div>

      <Card className='bg-card text-card-foreground border-border'>
        <CardHeader className='pb-2'>
          <CardTitle className='text-sm font-semibold flex items-center gap-2'>
            <FileText size={16} strokeWidth={1.5} />
            Account activity
          </CardTitle>
        </CardHeader>
        <CardContent className='p-0'>
          {loading && (
            <div className='flex justify-center py-6'>
              <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            </div>
          )}
          {!loading && lineItems.length === 0 && (
            <p className='text-sm text-muted-foreground px-6 py-6'>No billing activity in this period.</p>
          )}
          {!loading && lineItems.length > 0 && (
            <div className='border-t border-border overflow-x-auto'>
              <Table>
                <TableHeader>
                  <TableRow className='border-border'>
                    <TableHead className='text-muted-foreground'>Date</TableHead>
                    <TableHead className='text-muted-foreground'>Type</TableHead>
                    <TableHead className='text-muted-foreground'>Reference</TableHead>
                    <TableHead className='text-muted-foreground'>Description</TableHead>
                    <TableHead className='text-muted-foreground text-right'>Debit</TableHead>
                    <TableHead className='text-muted-foreground text-right'>Credit</TableHead>
                    <TableHead className='text-muted-foreground text-right'>Balance</TableHead>
                    <TableHead className='text-muted-foreground w-12' />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineItems.map((item, index) => {
                    const docUrl = resolveLineDocumentUrl(item, invoiceDocsById);
                    const isRefundLine = REFUND_LINE_TYPES.has(item.type);
                    const showPending = item.status === 'pending';
                    return (
                      <TableRow key={`${item.stripeObjectId || item.reference}-${index}`} className='border-border'>
                        <TableCell className='text-foreground whitespace-nowrap'>
                          {formatStatementDate(item.date)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant='secondary'
                            className={`text-xs font-normal ${typeBadgeClass(item.type)}`}>
                            {getLineItemTypeLabel(item.type)}
                          </Badge>
                          {showPending && (
                            <span className='ml-1 text-xs text-muted-foreground'>Pending</span>
                          )}
                          {!showPending && shouldShowStatementStatus(item.status) && (
                            <span className='ml-1 text-xs text-warning capitalize'>{item.status}</span>
                          )}
                        </TableCell>
                        <TableCell className='text-foreground'>
                          {item.reference || '—'}
                          {isRefundLine &&
                            item.relatedInvoiceId &&
                            item.reference &&
                            !String(item.reference).startsWith('re_') && (
                              <span className='block text-xs text-muted-foreground'>Related invoice</span>
                            )}
                        </TableCell>
                        <TableCell className='text-foreground max-w-xs truncate' title={item.description}>
                          {item.description || '—'}
                        </TableCell>
                        <TableCell className='text-right text-foreground whitespace-nowrap'>
                          {item.debit ? formatStatementMoney(item.debit, currency) : '—'}
                        </TableCell>
                        <TableCell className='text-right text-foreground whitespace-nowrap'>
                          {item.credit ? formatStatementMoney(item.credit, currency) : '—'}
                        </TableCell>
                        <TableCell className='text-right font-medium text-foreground whitespace-nowrap'>
                          {formatStatementMoney(item.balance, currency)}
                        </TableCell>
                        <TableCell>
                          {docUrl && (
                            <a
                              href={docUrl}
                              target='_blank'
                              rel='noreferrer'
                              className='text-primary hover:text-primary/80 inline-flex'
                              title={isRefundLine ? 'Open related invoice' : 'Open invoice'}>
                              <ExternalLink size={14} strokeWidth={1.5} />
                            </a>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {statement.generatedAt && (
        <p className='text-xs text-muted-foreground'>
          Generated {formatStatementDate(statement.generatedAt)}
        </p>
      )}
    </div>
  );
};

export default BillingStatementView;
